import { Divider, Typography } from '@remarkable/ark-web';

import { useState } from 'react';
import type { FleetDevice } from 'src/api/endpoints/cloudEnterprise.types';
import { useSendCommandRemoteWipe } from 'src/api/queries/enterprisePolicies';
import { Button, Input, Modal, NotificationBox } from 'src/components';
import { DeviceIcon } from 'src/components/DeviceIcon';
import { DeviceName } from 'src/components/DeviceName';

type DeviceActionTriggerRemoteWipeContentProps = {
  device: FleetDevice;
  userEmail: string | undefined;
  userId: string;
  onSuccess?: () => void;
};

export function DeviceActionTriggerRemoteWipe({
  device,
  userEmail,
  userId,
  onSuccess,
}: DeviceActionTriggerRemoteWipeContentProps) {
  const sendCommandRemoteWipe = useSendCommandRemoteWipe();
  const [confirmInput, setConfirmInput] = useState('');
  const isConfirmed = confirmInput.toLowerCase() === 'remote wipe';
  return (
    <Modal.Content title="Perform remote wipe?">
      <>
        <div className="flex flex-col gap-y-24 lm:gap-y-40">
          <div>
            <Typography variant="body-lg" fontWeight="semibold" as="div">
              <DeviceIcon
                device={device}
                size={24}
                className="mr-6 inline align-sub"
              />
              <b>
                <DeviceName device={device} />
              </b>{' '}
              / <span className="whitespace-nowrap">{device.serialNumber}</span>
            </Typography>

            <Typography variant="body-lg" className="mt-8" as="div">
              (paired with {userEmail})
            </Typography>
          </div>
          <Divider />
          <div className="mx-auto flex flex-col gap-y-24 lm:gap-y-32">
            <Typography variant="body-sm" as="div">
              This will reset the device to its factory settings. It may take up
              to an hour for the device to begin resetting. Here&apos;s what to
              expect:
              <ul className="mt-20 flex list-inside list-disc flex-col items-start text-left">
                <li>All data and settings will be deleted.</li>
                <li>The device will be removed from your device overview.</li>
                <li>
                  Previously synced files will remain safe in your cloud
                  storage.
                </li>
              </ul>
            </Typography>

            <Input
              label={
                <Typography variant="body-md" className="text-left">
                  Please type <b>remote wipe</b> to confirm
                </Typography>
              }
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.currentTarget.value)}
              name="confirm-wipe"
              data-cy="confirm-wipe"
              className="w-full"
              required
              maxLength={150}
            />
          </div>
          <div className="flex justify-center gap-16">
            <Modal.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Modal.Close>

            <Button
              variant="primary"
              loading={sendCommandRemoteWipe.isPending}
              disabled={!isConfirmed}
              onClick={() => {
                sendCommandRemoteWipe.mutate(
                  {
                    deviceId: device.serialNumber,
                    userId: userId,
                  },
                  { onSuccess }
                );
              }}
            >
              Confirm
            </Button>
          </div>
        </div>
        <NotificationBox
          error={sendCommandRemoteWipe.error}
          className="mt-16"
        />
      </>
    </Modal.Content>
  );
}
